import React, { useState, useEffect, useCallback } from 'react';
import { Trophy, Target, Calendar, BarChart3, TrendingUp, Users, Award, Star, X, ChevronLeft, ChevronRight, List, Clock, AlertCircle, Home } from 'lucide-react';
import { api } from '../api';
import './ProgressView.css';

// Skeleton components for progressive loading
const SkeletonStatCard = () => (
  <div className="stat-card-large skeleton-card">
    <div className="skeleton skeleton-icon" />
    <div className="stat-info">
      <div className="skeleton skeleton-value" />
      <div className="skeleton skeleton-label" />
    </div>
  </div>
);

const SkeletonChart = ({ height = 180 }) => (
  <div className="skeleton skeleton-chart" style={{ height }} />
);

const SkeletonRow = () => (
  <div className="skeleton skeleton-row" />
);

// Simple CSS Bar Chart Component
const BarChart = ({ data, labelKey, valueKey, color = '#e8c447', maxValue }) => {
  const max = maxValue || Math.max(...data.map(d => d[valueKey] || 0), 1);
  
  return (
    <div className="bar-chart">
      {data.map((item, index) => {
        const value = item[valueKey] || 0;
        const height = max > 0 ? (value / max) * 100 : 0;
        const label = item[labelKey];
        
        return (
          <div key={index} className="bar-item">
            <div className="bar-wrapper">
              <div 
                className="bar" 
                style={{ height: `${height}%`, backgroundColor: color }}
                title={`${label}: ${value}`}
              />
            </div>
            <span className="bar-label">{label}</span>
            <span className="bar-value">{value}</span>
          </div>
        );
      })}
    </div>
  );
};

// Horizontal Bar Chart for categories
const HorizontalBarChart = ({ data, onCategoryClick }) => {
  const max = Math.max(...data.map(d => d.points || 0), 1);
  
  return (
    <div className="horizontal-bar-chart">
      {data.map((item, index) => {
        const percentage = (item.points / max) * 100;
        const colors = {
          home: '#3b82f6',
          errands: '#8b5cf6',
          finance: '#10b981',
          health: '#ef4444',
          work: '#f59e0b',
          personal: '#6b7280',
          educational: '#ec4899'
        };
        
        return (
          <div 
            key={index} 
            className={`h-bar-item ${onCategoryClick ? 'clickable' : ''}`}
            onClick={() => onCategoryClick && onCategoryClick(item.category)}
            title={onCategoryClick ? `View ${item.category} chores` : ''}
          >
            <span className="h-bar-label">{item.category}</span>
            <div className="h-bar-wrapper">
              <div 
                className="h-bar" 
                style={{ width: `${percentage}%`, backgroundColor: colors[item.category] || '#e8c447' }}
              />
            </div>
            <span className="h-bar-value">{item.points} pts</span>
          </div>
        );
      })}
    </div>
  );
};

// Leaderboard Entry Component
const LeaderboardEntry = ({ entry, index }) => {
  const isTopThree = index < 3;
  const medals = ['🥇', '🥈', '🥉'];
  
  return (
    <div className={`leaderboard-entry ${entry.isCurrentUser ? 'current-user' : ''}`}>
      <div className="rank">
        {isTopThree ? (
          <span className="medal">{medals[index]}</span>
        ) : (
          <span className="rank-number">{entry.rank}</span>
        )}
      </div>
      <div className="user-info">
        <span className="user-name">{entry.name}</span>
        <span className={`user-role role-${entry.role}`}>{entry.role}</span>
      </div>
      <div className="stats">
        <div className="stat">
          <Star size={14} className="stat-icon" />
          <span>{entry.total_points} pts</span>
        </div>
        <div className="stat">
          <Target size={14} className="stat-icon" />
          <span>{entry.completed_tasks} tasks</span>
        </div>
      </div>
    </div>
  );
};

// Main Progress View Component
export default function ProgressView({ currentUser, onClose, onFilterChores, inline = false }) {
  const [activeTab, setActiveTab] = useState('overview');

  // Independent state per data source enables progressive loading
  const [progress, setProgress] = useState(null);
  const [progressLoading, setProgressLoading] = useState(true);
  const [progressError, setProgressError] = useState(null);

  const [leaderboard, setLeaderboard] = useState(null);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [leaderboardError, setLeaderboardError] = useState(null);
  const [leaderboardFilter, setLeaderboardFilter] = useState('current-month');

  const [adminReport, setAdminReport] = useState(null);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminError, setAdminError] = useState(null);

  // Admin report date filters - default to current month
  const [reportFilter, setReportFilter] = useState('current-month');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Generate list of past months (last 12) for the dropdown
  const pastMonths = React.useMemo(() => {
    const months = [];
    const now = new Date();
    // Start from previous month (skip current since it's a separate option)
    for (let i = 1; i <= 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const year = d.getFullYear();
      const month = d.getMonth();
      const start = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      // Last day of the month
      const lastDay = new Date(year, month + 1, 0).getDate();
      const end = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      const label = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      months.push({ value: `month:${start}`, label, start, end });
    }
    return months;
  }, []);

  // Load overview data on mount - non-blocking, UI already rendered
  const loadProgress = useCallback(async () => {
    setProgressLoading(true);
    setProgressError(null);
    try {
      const data = await api.getProgress();
      setProgress(data);
    } catch (err) {
      console.error('Failed to load progress:', err);
      setProgressError('Could not load progress data');
    } finally {
      setProgressLoading(false);
    }
  }, []);

  const loadLeaderboard = useCallback(async (filter = null) => {
    const useFilter = filter || leaderboardFilter;
    setLeaderboardLoading(true);
    setLeaderboardError(null);
    try {
      let apiFilter = useFilter;
      let apiStart = null;
      let apiEnd = null;

      // Convert past-month value to custom date range
      if (useFilter && useFilter.startsWith('month:')) {
        const monthStart = useFilter.slice(6);
        const monthOption = pastMonths.find(m => m.start === monthStart);
        if (monthOption) {
          apiStart = monthOption.start;
          apiEnd = monthOption.end;
          apiFilter = 'custom';
        }
      }

      const data = await api.getLeaderboard(apiFilter, apiStart, apiEnd);
      setLeaderboard(data.leaderboard || []);
    } catch (err) {
      console.error('Failed to load leaderboard:', err);
      setLeaderboardError('Could not load leaderboard');
    } finally {
      setLeaderboardLoading(false);
    }
  }, [leaderboardFilter, pastMonths]);

  const handleLeaderboardFilterChange = (newFilter) => {
    setLeaderboardFilter(newFilter);
    loadLeaderboard(newFilter);
  };

  const loadAdminReport = useCallback(async (filter = null, start = null, end = null) => {
    if (currentUser?.role !== 'admin') return;
    setAdminLoading(true);
    setAdminError(null);
    try {
      let useFilter = filter || reportFilter;
      let useStart = start;
      let useEnd = end;

      // Handle past-month selection: convert to custom date range
      if (useFilter && useFilter.startsWith('month:')) {
        const monthStart = useFilter.slice(6); // 'YYYY-MM-01'
        const monthOption = pastMonths.find(m => m.start === monthStart);
        if (monthOption) {
          useStart = monthOption.start;
          useEnd = monthOption.end;
          useFilter = 'custom';
        }
      } else if (useFilter === 'custom') {
        useStart = useStart || customStartDate;
        useEnd = useEnd || customEndDate;
      }

      const report = await api.getAdminProgressReport(useFilter, useStart, useEnd);
      setAdminReport(report);
    } catch (err) {
      console.error('Failed to load admin report:', err);
      setAdminError('Could not load team report');
    } finally {
      setAdminLoading(false);
    }
  }, [currentUser, reportFilter, customStartDate, customEndDate, pastMonths]);

  // Load overview immediately; prefetch leaderboard in background after paint
  useEffect(() => {
    loadProgress();
    // Prefetch leaderboard after first paint (idle) so tab switch is instant
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 300));
    const id = idle(() => loadLeaderboard());
    return () => {
      if (window.cancelIdleCallback && typeof id === 'number') window.cancelIdleCallback(id);
    };
  }, [loadProgress, loadLeaderboard]);

  // Lazy-load tab data when the tab is opened (only if not already loaded)
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'leaderboard' && leaderboard === null) loadLeaderboard();
    if (tab === 'report' && adminReport === null) loadAdminReport();
  };

  const handleFilterChange = (newFilter) => {
    setReportFilter(newFilter);
    loadAdminReport(newFilter);
  };

  // Fill in missing days for weekly chart
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const today = new Date();
  const last7Days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(today);
    d.setDate(d.getDate() - (6 - i));
    return {
      label: days[d.getDay()],
      value: progress?.weeklyData?.find(w => 
        new Date(w.date).toDateString() === d.toDateString()
      )?.points || 0
    };
  });

  return (
    <div className={`progress-view-overlay${inline ? ' progress-view-inline' : ''}`}>
      <div className="progress-view">
        {/* Header */}
        <div className="progress-header">
          <h2 className="progress-title">
            <BarChart3 size={24} />
            Activity Progress
          </h2>
          <button className="home-btn" onClick={onClose}>
            <Home size={18} />
            <span>Dashboard</span>
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="progress-tabs">
          <button 
            className={activeTab === 'overview' ? 'active' : ''}
            onClick={() => handleTabChange('overview')}
          >
            <TrendingUp size={16} />
            Overview
          </button>
          <button 
            className={activeTab === 'leaderboard' ? 'active' : ''}
            onClick={() => handleTabChange('leaderboard')}
          >
            <Trophy size={16} />
            Leaderboard
          </button>
          {currentUser?.role === 'admin' && (
            <button 
              className={activeTab === 'report' ? 'active' : ''}
              onClick={() => handleTabChange('report')}
            >
              <Users size={16} />
              Team Report
            </button>
          )}
        </div>

        {/* Content */}
        <div className="progress-content">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="overview-tab">
              {progressError && (
                <div className="section-error">
                  {progressError}
                  <button onClick={loadProgress} className="retry-btn">Retry</button>
                </div>
              )}

              {/* Stats Cards - show skeletons while loading */}
              <div className="stats-row">
                {progressLoading && !progress ? (
                  <>
                    <SkeletonStatCard />
                    <SkeletonStatCard />
                    <SkeletonStatCard />
                    <SkeletonStatCard />
                    <SkeletonStatCard />
                  </>
                ) : (
                <>
                <div 
                  className={`stat-card-large ${onFilterChores ? 'clickable' : ''}`}
                  onClick={() => onFilterChores && onFilterChores({ status: 'all' })}
                  title={onFilterChores ? 'View all chores' : ''}
                >
                  <List size={32} className="stat-icon" />
                  <div className="stat-info">
                    <span className="stat-value">{progress?.totalChores || 0}</span>
                    <span className="stat-label">Total</span>
                  </div>
                </div>
                <div 
                  className={`stat-card-large ${onFilterChores ? 'clickable' : ''}`}
                  onClick={() => onFilterChores && onFilterChores({ status: 'pending' })}
                  title={onFilterChores ? 'View pending chores' : ''}
                >
                  <Clock size={32} className="stat-icon" />
                  <div className="stat-info">
                    <span className="stat-value">{progress?.pendingCount || 0}</span>
                    <span className="stat-label">Pending</span>
                  </div>
                </div>
                <div 
                  className={`stat-card-large ${onFilterChores ? 'clickable' : ''}`}
                  onClick={() => onFilterChores && onFilterChores({ status: 'completed' })}
                  title={onFilterChores ? 'View completed chores' : ''}
                >
                  <Target size={32} className="stat-icon" />
                  <div className="stat-info">
                    <span className="stat-value">{progress?.completedCount || 0}</span>
                    <span className="stat-label">Complete</span>
                  </div>
                </div>
                <div 
                  className={`stat-card-large ${onFilterChores ? 'clickable' : ''}`}
                  onClick={() => onFilterChores && onFilterChores({ priority: 'high' })}
                  title={onFilterChores ? 'View high priority chores' : ''}
                >
                  <AlertCircle size={32} className="stat-icon" />
                  <div className="stat-info">
                    <span className="stat-value">{progress?.highPriorityCount || 0}</span>
                    <span className="stat-label">High Priority</span>
                  </div>
                </div>
                <div 
                  className={`stat-card-large ${onFilterChores ? 'clickable' : ''}`}
                  onClick={() => onFilterChores && onFilterChores({ dueToday: true })}
                  title={onFilterChores ? 'View chores due today' : ''}
                >
                  <Calendar size={32} className="stat-icon" />
                  <div className="stat-info">
                    <span className="stat-value">{progress?.dueTodayCount || 0}</span>
                    <span className="stat-label">Due Today</span>
                  </div>
                </div>
                </>
                )}
              </div>

              {/* Weekly Chart */}
              <div className="chart-section">
                <h3>
                  <Calendar size={18} />
                  Last 7 Days
                </h3>
                {progressLoading && !progress ? (
                  <SkeletonChart height={180} />
                ) : (
                  <BarChart 
                    data={last7Days} 
                    labelKey="label" 
                    valueKey="value"
                    color="#e8c447"
                  />
                )}
              </div>

              {/* Category Breakdown */}
              {progressLoading && !progress ? (
                <div className="chart-section">
                  <h3>
                    <BarChart3 size={18} />
                    Points by Category
                  </h3>
                  <SkeletonChart height={140} />
                </div>
              ) : progress?.categoryData?.length > 0 && (
                <div className="chart-section">
                  <h3>
                    <BarChart3 size={18} />
                    Points by Category
                  </h3>
                  <HorizontalBarChart 
                    data={progress.categoryData} 
                    onCategoryClick={(category) => onFilterChores && onFilterChores({ category })}
                  />
                </div>
              )}
            </div>
          )}

          {/* Leaderboard Tab */}
          {activeTab === 'leaderboard' && (
            <div className="leaderboard-tab">
              <div className="leaderboard-header">
                <h3>Top Performers</h3>
                <p>Based on total points earned</p>
              </div>

              {/* Date Filter - same options as Team Report */}
              <div className="report-filters">
                <label>Period:</label>
                <select
                  value={leaderboardFilter}
                  onChange={(e) => handleLeaderboardFilterChange(e.target.value)}
                  className="filter-select"
                >
                  <option value="current-month">Current Month</option>
                  <option value="current-week">Current Week</option>
                  <option value="last-week">Last Week</option>
                  <optgroup label="Past Months">
                    {pastMonths.map(m => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </optgroup>
                  <option value="all-time">All Time</option>
                </select>

                {(() => {
                  if (leaderboardFilter.startsWith('month:')) {
                    const monthOption = pastMonths.find(m => m.value === leaderboardFilter);
                    return monthOption ? <span className="filter-badge">{monthOption.label}</span> : null;
                  }
                  if (leaderboardFilter === 'current-month') {
                    const label = new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                    return <span className="filter-badge">{label}</span>;
                  }
                  if (leaderboardFilter !== 'all-time') {
                    return <span className="filter-badge">{leaderboardFilter.replace('-', ' ')}</span>;
                  }
                  return null;
                })()}
              </div>

              {leaderboardError && (
                <div className="section-error">
                  {leaderboardError}
                  <button onClick={loadLeaderboard} className="retry-btn">Retry</button>
                </div>
              )}
              <div className="leaderboard-list">
                {leaderboardLoading && !leaderboard ? (
                  <>
                    <SkeletonRow />
                    <SkeletonRow />
                    <SkeletonRow />
                    <SkeletonRow />
                    <SkeletonRow />
                  </>
                ) : (
                  (leaderboard || []).map((entry, index) => (
                    <LeaderboardEntry key={entry.id} entry={entry} index={index} />
                  ))
                )}
              </div>
            </div>
          )}

          {/* Admin Report Tab */}
          {activeTab === 'report' && currentUser?.role === 'admin' && adminLoading && !adminReport && (
            <div className="report-tab">
              <SkeletonChart height={60} />
              <div className="stats-row">
                <SkeletonStatCard />
                <SkeletonStatCard />
              </div>
              <SkeletonChart height={180} />
              <SkeletonRow />
              <SkeletonRow />
              <SkeletonRow />
            </div>
          )}

          {activeTab === 'report' && currentUser?.role === 'admin' && adminError && (
            <div className="section-error">
              {adminError}
              <button onClick={() => loadAdminReport()} className="retry-btn">Retry</button>
            </div>
          )}

          {activeTab === 'report' && currentUser?.role === 'admin' && adminReport && (
            <div className="report-tab">
              {/* Date Filter */}
              <div className="report-filters">
                <label>Period:</label>
                <select 
                  value={reportFilter} 
                  onChange={(e) => handleFilterChange(e.target.value)}
                  className="filter-select"
                >
                  <option value="current-month">Current Month</option>
                  <option value="current-week">Current Week</option>
                  <option value="last-week">Last Week</option>
                  <optgroup label="Past Months">
                    {pastMonths.map(m => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </optgroup>
                  <option value="all-time">All Time</option>
                  <option value="custom">Custom Range</option>
                </select>
                
                {reportFilter === 'custom' && (
                  <div className="custom-date-range">
                    <input 
                      type="date" 
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      placeholder="Start Date"
                    />
                    <span>to</span>
                    <input 
                      type="date" 
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      placeholder="End Date"
                    />
                    <button 
                      onClick={() => loadAdminReport('custom', customStartDate, customEndDate)}
                      disabled={!customStartDate || !customEndDate}
                      className="apply-btn"
                    >
                      Apply
                    </button>
                  </div>
                )}
                
                {(() => {
                  // Show a friendly label for current selection
                  if (reportFilter.startsWith('month:')) {
                    const monthOption = pastMonths.find(m => m.value === reportFilter);
                    return monthOption ? <span className="filter-badge">{monthOption.label}</span> : null;
                  }
                  if (reportFilter === 'current-month') {
                    const label = new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                    return <span className="filter-badge">{label}</span>;
                  }
                  if (adminReport.filter && adminReport.filter !== 'all-time') {
                    return <span className="filter-badge">{adminReport.filter.replace('-', ' ')}</span>;
                  }
                  return null;
                })()}
              </div>

              {/* Group Summary */}
              <div className="stats-row">
                <div className="stat-card-large">
                  <Users size={32} className="stat-icon" />
                  <div className="stat-info">
                    <span className="stat-value">{adminReport.groupTotals?.total_group_points || 0}</span>
                    <span className="stat-label">Team Total Points</span>
                  </div>
                </div>
                <div className="stat-card-large">
                  <Target size={32} className="stat-icon" />
                  <div className="stat-info">
                    <span className="stat-value">{adminReport.groupTotals?.total_completions || 0}</span>
                    <span className="stat-label">Team Completions</span>
                  </div>
                </div>
              </div>

              {/* Daily Chart */}
              {adminReport.dailyChart?.length > 0 && (
                <div className="chart-section">
                  <h3>
                    <Calendar size={18} />
                    {(() => {
                      if (reportFilter.startsWith('month:')) {
                        const m = pastMonths.find(x => x.value === reportFilter);
                        return m ? `${m.label} Activity` : 'Activity';
                      }
                      if (reportFilter === 'current-month') {
                        return `${new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} Activity`;
                      }
                      return adminReport.filter === 'all-time' ? 'Last 30 Days' : `${adminReport.filter.replace('-', ' ')} Activity`;
                    })()}
                  </h3>
                  <BarChart 
                    data={adminReport.dailyChart.map(d => ({
                      label: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                      value: d.points
                    }))} 
                    labelKey="label" 
                    valueKey="value"
                    color="#3b82f6"
                  />
                </div>
              )}

              {/* User Details Table */}
              <div className="chart-section">
                <h3>
                  <Users size={18} />
                  Team Member Stats
                </h3>
                <div className="user-stats-table">
                  <div className="table-header">
                    <span>Rank</span>
                    <span>Name</span>
                    <span>Points</span>
                    <span>Tasks</span>
                  </div>
                  {adminReport.users?.map((user, index) => (
                    <div key={user.id} className={`table-row ${user.id === currentUser.id ? 'current-user' : ''}`}>
                      <span className="rank">{index + 1}</span>
                      <span className="name">{user.name}</span>
                      <span className="points">{user.total_points}</span>
                      <span className="tasks">{user.completed_tasks}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
